import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Pagination } from '@core/models/pagination.model';
import { Promotion } from '@core/models/promotion.model';
import { AppPermissionService } from '@core/services/app-permission.service';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { PromotionCodeDataService } from '../promotion-codes/services/promotion-code-data.service';
import { PromotionCodeEntityService } from '../promotion-codes/services/promotion-code-entity.service';
import { TimezoneDatePipe } from '@core/pipes/timezone-date.pipe';
import { PromoTypePipe } from '@core/pipes/promo-type.pipe';
import { Status } from '@core/enums/status.enum';
import * as moment from 'moment-timezone';

@Component({
  selector: 'kt-archived-promotion-code',
  templateUrl: './archived-promotion-code.component.html',
})
export class ArchivedPromotionCodeComponent implements OnInit, OnDestroy {

  dropdown = {
    perPage: this.dropdownHttpService.perPage,
  };

  form: FormGroup;
  pagination: Pagination;
  pageSize = 30;
  page = 1;
  maxSize = 5;
  params = '';
  loading = false;
  searchBtnLoading = false;
  clearBtnLoading = false;
  dataLength = 0;

  archivedPromotions$: Observable<Promotion[]>;

  status = Status;

  canRestorePromotionCode: boolean;

  messages$ = this.promotionCodeDataService.messages$;

  private subscription = new Subscription();
  private subscriptions = new Subscription();

  constructor(
    private promotionCodeDataService: PromotionCodeDataService,
    private promotionCodeEntityService: PromotionCodeEntityService,
    private appPermissionService: AppPermissionService,
    private dropdownHttpService: DropdownHttpService,
    private loadingBar: LoadingBarService,
    private router: Router,
    private timeZoneDate: TimezoneDatePipe,
    private promoTypePipe: PromoTypePipe,
  ) {}

  timezoneDate(date: any, format: any) {
    return this.timeZoneDate.transform(date, format);
  }

  promoType(code: any, row: any) {
    return this.promoTypePipe.transform(code, row.promo_type, row.promo_sub_type);
  }

  isValidDate(date: Date | string) {
    return moment(date).isValid();
  }

  ngOnInit(): void {
    this.form = new FormGroup({
      id: new FormControl(null),
      promotion: new FormControl(null),
    });

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canRestorePromotionCode = appPermissions.restore_promotion_code;
    });
    this.subscriptions.add(apSub);

    this.onViewPageBy();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  onViewPageBy(page: number = 1) {
    this.loading = true;
    const query = `?page=${page}&perPage=${this.pageSize}${this.params ? '&' + this.params : ''}`;
    this.loadingBar.start();
    return this.archivedPromotions$ = this.promotionCodeDataService.getArchived(query).pipe(
      tap(res => {
        this.loading = false;
        this.dataLength = res.length;
        this.pagination = this.promotionCodeDataService.pagination;
        this.loadingBar.complete();
      })
    );
  }

  onSubmit() {
    this.searchBtnLoading = true;
    const formValues: any = { ...this.form.value };

    // Resolve the typeahead value into a backend-friendly `code` filter.
    // Selected from dropdown → object with .code/.name; free-typed → raw string.
    if (formValues.promotion) {
      if (typeof formValues.promotion === 'object' && formValues.promotion.code !== undefined) {
        formValues.code = formValues.promotion.code;
      } else {
        formValues.code = formValues.promotion;
      }
    }

    const parts: string[] = [];
    if (formValues.id) parts.push(`id=${formValues.id}`);
    if (formValues.code) parts.push(`code=${encodeURIComponent(formValues.code)}`);
    this.params = parts.join('&');
    this.page = 1;
    this.onViewPageBy(this.page);
    this.searchBtnLoading = false;
  }

  onClear() {
    this.clearBtnLoading = true;
    this.form.reset();
    this.params = '';
    this.page = 1;
    this.onViewPageBy(this.page);
    this.clearBtnLoading = false;
  }

  onPerPage(size: Event) {
    this.pageSize = +(size.target as HTMLSelectElement).value;
    this.onViewPageBy(this.page);
  }

  onRestore(row: any) {
    Swal.fire({
      title: 'Are you sure?',
      html: `Are you sure you want to restore promotion "<b>${row.name}</b>"?`,
      icon: 'info',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      showCancelButton: true,
      confirmButtonText: 'Yes, restore it',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    }).then((resp) => {
      if (resp.value) {
        this.loadingBar.start();
        this.subscription = this.promotionCodeDataService.restore(row.id).subscribe(() => {
          this.loadingBar.complete();
          this.onViewPageBy(this.page);
        });
      }
    });
  }

  onBack() {
    this.router.navigate(['/general/promotion-codes']);
  }

  searchPromo = (search$: Observable<string>): Observable<Promotion[]> => {
    return search$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => term.trim() === '' ? of([]) : this.promotionCodeEntityService.getWithQuery(`?code=${term}&name=${term}&typeahead_search=${1}&only_trashed=1`)),
      catchError(() => of([]))
    );
  }

  formatPromo(promotion: Promotion): string {
    return `${promotion.code} - ${promotion.name}`;
  }
}
